import { useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQRStore, QRType } from '@/stores/qrStore';
import { WhatsAppForm } from './forms/WhatsAppForm';
import { PhoneCallForm } from './forms/PhoneCallForm';
import { SMSForm } from './forms/SMSForm';
import { LocationForm } from './forms/LocationForm';
import { VCardForm } from './forms/VCardForm';
import { BioPageForm } from './forms/BioPageForm';
import { ARForm } from './forms/ARForm';
import { AIArtForm } from './forms/AIArtForm';
import { GamificationForm } from './forms/GamificationForm';
import { BlockchainForm } from './forms/BlockchainForm';
import { FileUploadField } from './forms/FileUploadField';

const formConfigs: Record<QRType, { fields: { name: string; label: string; type: string; placeholder: string; required?: boolean }[] }> = {
  url: {
    fields: [
      { name: 'url', label: 'URL', type: 'url', placeholder: 'https://example.com', required: true },
    ],
  },
  // vCard uses custom form now
  vcard: { fields: [] },
  wifi: {
    fields: [
      { name: 'ssid', label: 'Network Name (SSID)', type: 'text', placeholder: 'MyWiFi', required: true },
      { name: 'password', label: 'Password', type: 'text', placeholder: 'secretpassword' },
      { name: 'encryption', label: 'Encryption', type: 'select', placeholder: 'WPA/WPA2' },
    ],
  },
  email: {
    fields: [
      { name: 'email', label: 'Email Address', type: 'email', placeholder: 'hello@example.com', required: true },
      { name: 'subject', label: 'Subject', type: 'text', placeholder: 'Hello!' },
      { name: 'body', label: 'Message', type: 'textarea', placeholder: 'Your message here...' },
    ],
  },
  // These use custom forms now
  sms: { fields: [] },
  whatsapp: { fields: [] },
  phone: { fields: [] },
  location: { fields: [] },
  text: {
    fields: [
      { name: 'text', label: 'Text Content', type: 'textarea', placeholder: 'Enter your text here...', required: true },
    ],
  },
  calendar: {
    fields: [
      { name: 'title', label: 'Event Title', type: 'text', placeholder: 'Meeting', required: true },
      { name: 'startDate', label: 'Start Date & Time', type: 'datetime-local', placeholder: '', required: true },
      { name: 'endDate', label: 'End Date & Time', type: 'datetime-local', placeholder: '' },
      { name: 'location', label: 'Location', type: 'text', placeholder: 'Conference Room A' },
      { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Event details...' },
    ],
  },
  paypal: {
    fields: [
      { name: 'username', label: 'PayPal.me Username', type: 'text', placeholder: 'yourname', required: true },
      { name: 'amount', label: 'Amount (optional)', type: 'number', placeholder: '10.00' },
    ],
  },
  bitcoin: {
    fields: [
      { name: 'address', label: 'Bitcoin Address', type: 'text', placeholder: 'bc1q...', required: true },
      { name: 'amount', label: 'Amount (optional)', type: 'text', placeholder: '0.001' },
    ],
  },
  appstore: {
    fields: [
      { name: 'url', label: 'App Store / Play Store URL', type: 'url', placeholder: 'https://apps.apple.com/...', required: true },
    ],
  },
  pdf: { fields: [] },
  instagram: {
    fields: [
      { name: 'username', label: 'Instagram Username', type: 'text', placeholder: 'yourhandle', required: true },
    ],
  },
  twitter: {
    fields: [
      { name: 'username', label: 'Twitter/X Username', type: 'text', placeholder: 'yourhandle', required: true },
    ],
  },
  linkedin: {
    fields: [
      { name: 'username', label: 'LinkedIn Username', type: 'text', placeholder: 'john-doe', required: true },
    ],
  },
  ar: { fields: [] },
  bio_page: { fields: [] },
  ai_art: { fields: [] },
  gamification: { fields: [] },
  blockchain: { fields: [] },
};

// Types that use custom specialized forms
const customFormTypes: QRType[] = ['whatsapp', 'phone', 'sms', 'location', 'vcard', 'ar', 'bio_page', 'ai_art', 'gamification', 'blockchain', 'pdf'];

export function QRContentForm() {
  const { currentQR, setContentData } = useQRStore();
  const config = formConfigs[currentQR.type];

  const { register, watch, setValue } = useForm({
    defaultValues: currentQR.contentData,
  });

  const encryptionValue = watch('encryption');

  const lastSentRef = useRef<Record<string, unknown>>({});

  const shallowEqual = (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if (a[k] !== b[k]) return false;
    }
    return true;
  };

  // Memoized update handler for custom forms
  const handleCustomFormUpdate = useCallback((data: Record<string, string>) => {
    setContentData(data);
  }, [setContentData]);

  // Sync form -> store without causing a feedback loop.
  // Using a watch subscription avoids re-rendering the whole component on each keystroke,
  // and we DO NOT re-apply store values back into the form on every store update.
  useEffect(() => {
    if (customFormTypes.includes(currentQR.type)) return;

    const subscription = watch((values) => {
      const next = (values || {}) as Record<string, unknown>;
      if (shallowEqual(next, lastSentRef.current)) return;
      lastSentRef.current = next;
      setContentData(next as Record<string, string>);
    });

    return () => subscription.unsubscribe();
  }, [watch, setContentData, currentQR.type]);

  // Reset form when *type* changes (not when contentData changes).
  useEffect(() => {
    if (customFormTypes.includes(currentQR.type)) return;

    const nextDefaults: Record<string, unknown> = {};
    config.fields.forEach((field) => {
      const v = (currentQR.contentData as Record<string, unknown>)[field.name] ?? '';
      nextDefaults[field.name] = v;
      setValue(field.name, v as any, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
    });
    lastSentRef.current = nextDefaults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQR.type]);

  // Render custom forms for specific types
  if (currentQR.type === 'whatsapp') {
    return (
      <WhatsAppForm 
        contentData={currentQR.contentData} 
        onUpdate={handleCustomFormUpdate}
      />
    );
  }

  if (currentQR.type === 'phone') {
    return (
      <PhoneCallForm 
        contentData={currentQR.contentData} 
        onUpdate={handleCustomFormUpdate}
      />
    );
  }

  if (currentQR.type === 'sms') {
    return (
      <SMSForm 
        contentData={currentQR.contentData} 
        onUpdate={handleCustomFormUpdate}
      />
    );
  }

  if (currentQR.type === 'location') {
    return (
      <LocationForm 
        contentData={currentQR.contentData} 
        onUpdate={handleCustomFormUpdate}
      />
    );
  }

  if (currentQR.type === 'vcard') {
    return (
      <VCardForm 
        contentData={currentQR.contentData} 
        onUpdate={handleCustomFormUpdate}
      />
    );
  }

  if (currentQR.type === 'bio_page') {
    return (
      <BioPageForm 
        contentData={currentQR.contentData} 
        onUpdate={handleCustomFormUpdate}
      />
    );
  }

  if (currentQR.type === 'ar') {
    return (
      <ARForm 
        contentData={currentQR.contentData} 
        onUpdate={handleCustomFormUpdate}
      />
    );
  }

  if (currentQR.type === 'ai_art') {
    return (
      <AIArtForm 
        contentData={currentQR.contentData} 
        onUpdate={handleCustomFormUpdate}
      />
    );
  }

  if (currentQR.type === 'gamification') {
    return (
      <GamificationForm 
        contentData={currentQR.contentData} 
        onUpdate={handleCustomFormUpdate}
      />
    );
  }

  if (currentQR.type === 'pdf') {
    return (
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Content
        </h3>
        <FileUploadField
          label="PDF File"
          value={currentQR.contentData.url || ''}
          onChange={(url) => handleCustomFormUpdate({ url })}
          fileType="pdf"
          placeholder="https://example.com/document.pdf"
          required
        />
      </div>
    );
  }

  if (currentQR.type === 'blockchain') {
    return (
      <BlockchainForm 
        contentData={currentQR.contentData} 
        onUpdate={handleCustomFormUpdate}
      />
    );
  }

  // Default form for other types
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentQR.type}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="space-y-4"
      >
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Content
        </h3>
        
        <div className="space-y-4">
          {config.fields.map((field) => (
            <div key={field.name} className="space-y-2">
              <Label htmlFor={field.name}>
                {field.label}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </Label>
              
              {field.type === 'textarea' ? (
                <Textarea
                  id={field.name}
                  placeholder={field.placeholder}
                  className="min-h-[80px] resize-none"
                  {...register(field.name)}
                />
              ) : field.type === 'select' && field.name === 'encryption' ? (
                <Select
                  value={(encryptionValue as string) || 'WPA'}
                  onValueChange={(value) =>
                    setValue('encryption', value, { shouldDirty: true, shouldTouch: true })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select encryption" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WPA">WPA/WPA2</SelectItem>
                    <SelectItem value="WEP">WEP</SelectItem>
                    <SelectItem value="nopass">None</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={field.name}
                  type={field.type}
                  placeholder={field.placeholder}
                  {...register(field.name)}
                />
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
